import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './ShopAccountSummary.styles';
import { AngleIcon } from '@/components/UI/Icons/Icons';
import { useResponsive } from '@/hooks/useResponsive';
import { formatNumber } from '@/utils/helpers';
import { useBusinessBalance } from '@/features/Profile/core/hooks';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { BalanceSplitModal } from '@/features/Profile/core/BalanceSplitModal';

interface ShopAccountSummaryProps {
  isActiveShop: boolean;
  balance: number;
  isBalanceLoading?: boolean;
  onAddBalance?: () => void;
}

export const ShopAccountSummary = ({
  balance,
  isActiveShop,
  isBalanceLoading,
  onAddBalance,
}: ShopAccountSummaryProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const {
    balances,
    adsBalance,
    loading: isAdsBalanceLoading,
  } = useBusinessBalance();
  const [isBalanceSplitOpen, toggleBalanceSplit] = useToggleOpenState(false);

  function openBalanceSplit() {
    toggleBalanceSplit(true);
  }

  function closeBalanceSplit() {
    toggleBalanceSplit(false);
  }

  function handleModalTopUp() {
    onAddBalance?.();
  }

  return (
    <>
      <S.Grid $isDesktop={isDesktop} $isActiveShop={isActiveShop}>
        {isActiveShop && (
          <S.PackageCard>
            <S.Header>
              <S.Title>Silver</S.Title>
              <S.DetailsButton>
                <AngleIcon />
              </S.DetailsButton>
            </S.Header>
            <S.PackageDetails>
              <S.Label>{t('pages.cabinet.expiration_date')}</S.Label>
              <S.Date>29.04.2026</S.Date>
            </S.PackageDetails>
            <S.RenewButton isFullWidth borderRadius={14}>
              {t('pages.cabinet.renew_package')}
            </S.RenewButton>
          </S.PackageCard>
        )}

        <S.AccountCard $isActiveShop={isActiveShop}>
          <S.Header $isCompact={!isActiveShop}>
            <S.Label>{t('pages.cabinet.private_account')}</S.Label>
            <S.DetailsButton
              onClick={openBalanceSplit}
              aria-label={t('pages.cabinet.balance_split')}
            >
              <AngleIcon />
            </S.DetailsButton>
          </S.Header>
          {isBalanceLoading ? (
            <S.BalanceSkeleton $isCompact={!isActiveShop} />
          ) : (
            <S.Amount $isCompact={!isActiveShop}>
              {formatNumber(balance, 2)} ₼
            </S.Amount>
          )}
          <S.ActionButton $isCompact={!isActiveShop} onClick={onAddBalance}>
            {t('common.add_balance_btn')}
          </S.ActionButton>
        </S.AccountCard>

        {isActiveShop && (
          <S.AdsBalanceCard>
            <S.Header>
              <S.Label>{t('pages.cabinet.ads_balance')}</S.Label>
            </S.Header>
            {isAdsBalanceLoading ? (
              <S.BalanceSkeleton />
            ) : (
              <S.Amount>
                {adsBalance.amount === undefined
                  ? ''
                  : formatNumber(adsBalance.amount, 0)}
              </S.Amount>
            )}
            <S.Description>
              {t('pages.cabinet.remaining_ads_in_package')}
            </S.Description>
          </S.AdsBalanceCard>
        )}
      </S.Grid>

      <BalanceSplitModal
        isOpen={isBalanceSplitOpen}
        onClose={closeBalanceSplit}
        items={balances}
        onTopUp={handleModalTopUp}
      />
    </>
  );
};
